<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="classes">
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue"

// hi     – the one main action on a screen, in the taxi system's colour
// quiet  – grey fill, secondary
// solid  – ink background, used for the inline delete confirmation
// danger – grey fill with red text, destructive
// ghost  – transparent, text only; ghostDanger turns red on hover (row actions)
type Variant = "hi" | "quiet" | "solid" | "danger" | "ghost" | "ghostDanger"

interface Props {
  variant?: Variant
  type?: "button" | "submit"
  disabled?: boolean
  loading?: boolean
  block?: boolean
  size?: "md" | "lg" | "sm"
}

const props = withDefaults(defineProps<Props>(), {
  variant: "quiet",
  type: "button",
  disabled: false,
  loading: false,
  block: false,
  size: "md"
})

const classes = computed(() => {
  const base = "inline-flex items-center justify-center gap-2 rounded-[11px] border-0 leading-none transition-colors disabled:cursor-default"
  const size = {
    sm: "min-h-[36px] px-[13px] text-[14px]",
    md: "min-h-[44px] px-4 text-[15px]",
    lg: "min-h-[54px] px-5 text-[17px]"
  }[props.size]
  const variant = {
    hi: "bg-hi text-on-hi font-semibold disabled:bg-fill disabled:text-faint disabled:font-semibold",
    quiet: "bg-fill text-ink font-medium hover:bg-line/70 disabled:text-faint",
    solid: "bg-ink text-on-ink font-medium hover:bg-ink/90",
    danger: "bg-fill text-err font-medium hover:bg-err-bg",
    ghost: "bg-transparent text-muted hover:bg-fill hover:text-ink",
    ghostDanger: "bg-transparent text-muted hover:bg-fill hover:text-err"
  }[props.variant]
  return [base, size, variant, props.block && "flex w-full"]
})
</script>
